import { Component, ElementRef, EventEmitter, Input, NgZone, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { take, takeUntil } from "rxjs/operators";

import {
  BaseComponent,
  CommunitiesService,
  EventBusService,
  FeaturedMedia,
  HelperService,
  REACTION_EMOJI,
  REACTION_LABELS,
  REACTION_TYPES,
  ReactionType,
  UserService,
  setUserReactionsOnDoc,
  avatarColorFor,
  featuredMediaFor,
  getMyReactions,
  getReactionCounts,
  initialsFor,
  normalizeReactions,
  toggleReaction,
} from "@mypxplat/xplat/core";
import { AppService, CommunityReactionService, CommunityShareService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { SimpleSignupComponent } from "../../modals/simple-signup/simple-signup.component";

/** One card, resolved up front so the template binds plain fields. */
interface FeaturedCard {
  post: any;
  id: string;
  /** The post's own URL, so a card is a real link (middle-click, copy link address). */
  href: string;
  authorName: string;
  authorPhoto: string;
  initials: string;
  avatarColor: string;
  createdDate: Date | null;
  /** The post's subject, plain text; "" when it has none. */
  subject: string;
  bodyPreview: string;
  media: FeaturedMedia;
  /** "Watch video", "Play audio"…: the media tile's accessible name. */
  mediaLabel: string;
  /** From the parent's stored `reply_count`; the rail doesn't load replies. */
  replyCount: number;
  /** Up to three reaction emoji, most-used first, and how many reactions there are in all. */
  reactionEmoji: string[];
  reactionTotal: number;
  /**
   * The reader's own reaction, first in REACTION_TYPES order — the Like button shows it, as
   * Facebook's shows "Love" in red once you've loved a post. Null when they haven't reacted.
   */
  myReaction: ReactionType | null;
  myReactionEmoji: string;
  myReactionLabel: string;
  /** Every reaction the reader has left (several are allowed, as in the feed), for the bar's highlights. */
  myReactionSet: Partial<Record<ReactionType, boolean>>;
  unread: boolean;
}

/**
 * The Featured rail (DENG-1294): a community's moderator-picked posts, as a row of cards above
 * its feed. Like Facebook's featured posts in a group — the posts leave the feed and live here
 * until a moderator takes them out.
 *
 * Renders nothing when the community has no featured posts. The parent decides WHETHER the rail
 * belongs on the view (`featuredRailApplies`); this only fills it.
 *
 * A card opens its post the way a feed row does: it emits `openPost` and the host decides —
 * the split pane on a wide screen, the post's own page otherwise.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community-featured-rail",
  templateUrl: "featured-rail.component.html",
  styleUrls: ["featured-rail.component.scss"],
  standalone: false,
})
export class FeaturedRailComponent extends BaseComponent {
  /** A card was opened: the host opens it as it opens a feed row's post. */
  @Output() openPost = new EventEmitter<string>();

  @Input() set communityStringId(value: string) {
    if (value === this._communityStringId) return;
    this._communityStringId = value;
    this.cards = [];
    this.load();
  }
  get communityStringId(): string {
    return this._communityStringId;
  }
  private _communityStringId: string;

  /**
   * The scrolling row. Watched for size changes — a card added or removed, the window or the
   * pane resized — so the arrows only show while there is somewhere to scroll.
   */
  @ViewChild("track") set trackRef(ref: ElementRef<HTMLElement> | undefined) {
    this._trackObserver?.disconnect();
    this.track = ref;
    if (!ref || typeof ResizeObserver === "undefined") return;
    this._trackObserver = new ResizeObserver(() => this._zone.run(() => this.updateScrollState()));
    this._trackObserver.observe(ref.nativeElement);
  }
  public track: ElementRef<HTMLElement> | undefined;
  private _trackObserver: ResizeObserver | null = null;

  public readonly reactionTypes = REACTION_TYPES;
  public readonly reactionEmoji = REACTION_EMOJI;
  public readonly reactionLabels = REACTION_LABELS;
  /** The card whose reaction bar is open — one at a time. */
  public pickerCardId: string | null = null;
  /** Which reaction has keyboard focus in the open bar. */
  public pickerFocus = 0;
  private _openTimer: ReturnType<typeof setTimeout> | null = null;
  private _closeTimer: ReturnType<typeof setTimeout> | null = null;
  private _longPressTimer: ReturnType<typeof setTimeout> | null = null;
  /** A long press opened the bar, so the click that ends it mustn't also toggle Like. */
  private _longPressed = false;
  private readonly OPEN_DELAY_MS = 350;
  private readonly CLOSE_DELAY_MS = 250;
  private readonly LONG_PRESS_MS = 450;

  public cards: FeaturedCard[] = [];
  /** Moderators and admins of this community get "Remove from featured". */
  public canManage = false;
  public newCount = 0;
  /** Whether the arrows have anywhere to go — kept in step with the track's scroll. */
  public canScrollBack = false;
  public canScrollForward = false;

  private _loadSeq = 0;
  /** The card whose Share just copied its link — that button shows "Link copied" briefly. */
  public copiedCardId: string | null = null;
  private _copiedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    userService: UserService,
    public communityService: WebCommunityService,
    private _communitiesService: CommunitiesService,
    private _appService: AppService,
    private _reactionService: CommunityReactionService,
    private _helperService: HelperService,
    private _router: Router,
    private _eventBus: EventBusService,
    private _translate: TranslateService,
    private _zone: NgZone,
    private _shareService: CommunityShareService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => this._updatePermissions());
    this._communitiesService.communities$.pipe(takeUntil(this.destroy$)).subscribe(() => this._updatePermissions());
    this.communityService.featuredChanged$.pipe(takeUntil(this.destroy$)).subscribe(({ communityStringId }) => {
      if (communityStringId === this.communityStringId) this.load();
    });
    // Opening a card writes a view record; the "N new" count follows it.
    this.communityService.postViews$.pipe(takeUntil(this.destroy$)).subscribe(() => this._recomputeUnread());
  }

  load() {
    const stringId = this.communityStringId;
    if (!stringId) return;
    const seq = ++this._loadSeq;
    this.communityService.getFeaturedPosts(stringId).then((posts) => {
      // A newer load (another community, or a change) superseded this one.
      if (seq !== this._loadSeq) return;
      this._setCards(posts.map((post) => this._toCard(post)));
      this.communityService.prefetchPostViews(this.cards.map((c) => c.id)).then(() => this._recomputeUnread());
      this._recomputeUnread();
    });
  }

  open(card: FeaturedCard, event?: Event) {
    // Leave modified clicks to the browser — the card is a real link to the post.
    if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) return;
    event?.preventDefault();
    this.openPost.emit(card.id);
    // The detail marks the post read; reflect it on the card at once.
    card.unread = false;
    this._recountNew();
  }

  /** The card's •••: moderators only (`canManage`). Delete is a moderator's, as on the post's own menu. */
  cardOptions(card: FeaturedCard, anchor?: EventTarget) {
    const remove = "Remove from Featured";
    const del = "Delete Post";
    this._eventBus.emit(this._eventBus.types.showActionChooser, {
      title: anchor ? "" : this._helperService.stripHtml(card.post.message || "").substring(0, 200) + "...",
      actions: [
        { label: remove, icon: "thumbtack-slash" },
        { label: del, icon: "trash-alt" },
      ],
      anchor,
    });
    this._eventBus
      .observe(this._eventBus.types.actionChosen)
      .pipe(take(1))
      .subscribe((result) => {
        if (result === remove) this.removeFromFeatured(card);
        else if (result === del) this.deletePost(card);
      });
  }

  deletePost(card: FeaturedCard) {
    this._appService.confirm(this._translate.instant("com.confirm_delete_post")).then((ok) => {
      if (!ok) return;
      const previous = this.cards;
      this._setCards(this.cards.filter((c) => c.id !== card.id));
      this._recountNew();
      // `admin` marks a moderator removing someone else's post, as the post menu's "(Admin)" delete does.
      const admin = `${card.post.author?.id}` !== `${this.user?.id}`;
      this.communityService.deletePost(card.post.key, admin).catch((error) => {
        this._setCards(previous);
        this._recountNew();
        this._appService.alert(error?.message || "That post couldn't be deleted.");
      });
    });
  }

  removeFromFeatured(card: FeaturedCard) {
    this._appService.confirm("Remove this post from Featured? It goes back into the feed in the order it was posted.").then((ok) => {
      if (!ok) return;
      // Optimistic: the card goes now, and comes back if the write is refused.
      const previous = this.cards;
      this._setCards(this.cards.filter((c) => c.id !== card.id));
      this._recountNew();
      this.communityService.setPostFeatured(card.post, false).catch((error) => {
        this._setCards(previous);
        this._recountNew();
        this._appService.alert(error?.message || "That post couldn't be removed from Featured.");
      });
    });
  }

  scrollBy(direction: 1 | -1) {
    const el = this.track?.nativeElement;
    if (!el) return;
    const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: reduceMotion ? "auto" : "smooth" });
  }

  /** Every change to the cards goes through here, so the arrows are re-checked once it's drawn. */
  private _setCards(cards: FeaturedCard[]) {
    this.cards = cards;
    setTimeout(() => this.updateScrollState());
  }

  updateScrollState() {
    const el = this.track?.nativeElement;
    if (!el) return;
    this.canScrollBack = el.scrollLeft > 4;
    this.canScrollForward = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
  }

  // ---------- Like and the reaction bar ----------
  //
  // Facebook's rail: Like toggles your reaction, and resting on it (hover, a long press, or ↑
  // from the keyboard) opens the full row of reactions. The bar overlays the card itself,
  // across its width — the rail scrolls sideways, so anything poking out of a card is clipped.

  /** A plain click: off if you've reacted (whatever with), otherwise 👍. */
  onLikeClick(card: FeaturedCard) {
    if (this._longPressed) {
      this._longPressed = false;
      return;
    }
    this.closePicker();
    this.react(card, card.myReaction || "like");
  }

  onLikeEnter(card: FeaturedCard) {
    this._clearTimer("_closeTimer");
    if (this.pickerCardId === card.id) return;
    this._clearTimer("_openTimer");
    this._openTimer = setTimeout(() => this._openPicker(card), this.OPEN_DELAY_MS);
  }

  onLikeLeave() {
    this._clearTimer("_openTimer");
    this._clearTimer("_closeTimer");
    this._closeTimer = setTimeout(() => this.closePicker(), this.CLOSE_DELAY_MS);
  }

  /** The pointer moved from Like onto the bar: keep it open. */
  onPickerEnter() {
    this._clearTimer("_closeTimer");
  }

  onLikePointerDown(card: FeaturedCard, event: PointerEvent) {
    if (event.pointerType === "mouse") return;
    this._clearTimer("_longPressTimer");
    this._longPressed = false;
    this._longPressTimer = setTimeout(() => {
      this._longPressed = true;
      this._openPicker(card);
      // Touch has no "leave": a tap anywhere else closes it.
      setTimeout(() => document.addEventListener("pointerdown", this._outsidePointerDown, true));
    }, this.LONG_PRESS_MS);
  }

  onLikePointerUp() {
    this._clearTimer("_longPressTimer");
  }

  onLikeKeydown(card: FeaturedCard, event: KeyboardEvent) {
    if (event.key !== "ArrowUp") return;
    event.preventDefault();
    this._openPicker(card, true);
  }

  onPickerKeydown(card: FeaturedCard, event: KeyboardEvent) {
    const last = this.reactionTypes.length - 1;
    if (event.key === "ArrowRight") this.pickerFocus = this.pickerFocus >= last ? 0 : this.pickerFocus + 1;
    else if (event.key === "ArrowLeft") this.pickerFocus = this.pickerFocus <= 0 ? last : this.pickerFocus - 1;
    else if (event.key === "Home") this.pickerFocus = 0;
    else if (event.key === "End") this.pickerFocus = last;
    else if (event.key === "Escape") {
      event.preventDefault();
      this.closePicker(card);
      return;
    } else return;
    event.preventDefault();
    this._focusOption(card);
  }

  pick(card: FeaturedCard, type: ReactionType) {
    this.closePicker(card);
    this.react(card, type);
  }

  closePicker(returnFocusTo?: FeaturedCard) {
    this._clearTimer("_openTimer");
    this._clearTimer("_closeTimer");
    document.removeEventListener("pointerdown", this._outsidePointerDown, true);
    this.pickerCardId = null;
    if (returnFocusTo) setTimeout(() => (document.getElementById(`featured-like-${returnFocusTo.id}`) as HTMLElement | null)?.focus());
  }

  /** Toggles `type` for the reader through the same `CommunityReactionService` path as the feed. */
  react(card: FeaturedCard, type: ReactionType) {
    if (!this.user) {
      this._appService.promptSimpleSignup(SimpleSignupComponent).then(
        () => {},
        () => {}
      );
      return;
    }
    if (!this._reactionService.canReact(this.communityStringId)) {
      this._reactionService.explainBlock(this._reactionService.gateFor(this.communityStringId), this.communityStringId);
      return;
    }
    const post = card.post;
    const userId = String(this.user.id);
    const previousTypes = getMyReactions(post.reactions || {}, userId);
    const nextTypes = toggleReaction(previousTypes, type);
    // `reactions` AND the legacy `likes` together — see `setUserReactionsOnDoc`.
    setUserReactionsOnDoc(post, userId, nextTypes);
    this._reactionService.persist({
      post,
      selectedType: type,
      previousTypes,
      nextTypes,
      communityStringId: this.communityStringId,
      channelName: Array.isArray(post.channels) ? post.channels.join(",") : post.channels,
    });
    this._summarizeReactions(card);
  }

  async share(card: FeaturedCard) {
    const outcome = await this._shareService.share(card.post, { communityStringId: this._communityStringId });
    if (outcome !== "copied") return;
    this.copiedCardId = card.id;
    if (this._copiedTimer) clearTimeout(this._copiedTimer);
    this._copiedTimer = setTimeout(() => (this.copiedCardId = null), 2000);
  }

  ngOnDestroy() {
    this._trackObserver?.disconnect();
    if (this._copiedTimer) clearTimeout(this._copiedTimer);
    this.closePicker();
    this._clearTimer("_longPressTimer");
    super.ngOnDestroy();
  }

  private _openPicker(card: FeaturedCard, focus = false) {
    this._clearTimer("_openTimer");
    this.pickerCardId = card.id;
    const mine = card.myReaction ? this.reactionTypes.indexOf(card.myReaction) : 0;
    this.pickerFocus = mine > -1 ? mine : 0;
    if (focus) setTimeout(() => this._focusOption(card));
  }

  private _focusOption(card: FeaturedCard) {
    (document.getElementById(`featured-reaction-${card.id}-${this.pickerFocus}`) as HTMLElement | null)?.focus();
  }

  private _outsidePointerDown = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.(".featured-card__picker")) return;
    this.closePicker();
  };

  private _clearTimer(name: "_openTimer" | "_closeTimer" | "_longPressTimer") {
    if (this[name]) clearTimeout(this[name]);
    this[name] = null;
  }

  private _summarizeReactions(card: FeaturedCard) {
    const counts = getReactionCounts(card.post.reactions || {}).sort((a, b) => b.count - a.count);
    card.reactionEmoji = counts.slice(0, 3).map((c) => c.emoji);
    // Every reaction, not every reactor: a member can leave several (as the feed's chips
    // show), so counting people under-reported a post one person had loved, liked and 🔥'd.
    card.reactionTotal = counts.reduce((n, c) => n + c.count, 0);
    const mine = this.user ? getMyReactions(card.post.reactions || {}, String(this.user.id)) : [];
    card.myReaction = mine[0] || null;
    card.myReactionSet = mine.reduce((set, type) => ((set[type] = true), set), {} as Partial<Record<ReactionType, boolean>>);
    card.myReactionEmoji = card.myReaction ? REACTION_EMOJI[card.myReaction] : "";
    card.myReactionLabel = card.myReaction ? REACTION_LABELS[card.myReaction] : "";
  }

  onViewProfile(userId: string) {
    if (userId) this._router.navigate(["/profile", userId]);
  }

  private _updatePermissions() {
    this.canManage = !!this.user && this._communitiesService.isCommunityModerator(this.user, this.communityStringId);
  }

  private _toCard(post: any): FeaturedCard {
    const author = post.author || {};
    const authorName = author.name || "Member";
    const media = featuredMediaFor(post);
    // Normalised on the post itself, which the Like toggle and the opened post both read.
    post.reactions = normalizeReactions(post);
    const card: FeaturedCard = {
      post,
      id: String(post.id),
      href: `/community/${post.community_string_id || this.communityStringId}/post/${post.id}`,
      authorName,
      authorPhoto: author.photo || "",
      initials: initialsFor(authorName),
      avatarColor: avatarColorFor(author.id),
      createdDate: post.created?.toDate ? post.created.toDate() : post.created || null,
      subject: post.subject ? this._helperService.stripHtml(post.subject, false, true) || "" : "",
      bodyPreview: post.noHtmlMessageFlat || this._helperService.stripHtml(post.message || "", false, true) || "",
      media,
      mediaLabel: media.kind === "audio" ? "Play audio" : media.kind === "youtube" || media.kind === "video" ? "Watch video" : "",
      replyCount: typeof post.reply_count === "number" ? post.reply_count : 0,
      reactionEmoji: [],
      reactionTotal: 0,
      myReaction: null,
      myReactionEmoji: "",
      myReactionLabel: "",
      myReactionSet: {},
      unread: false,
    };
    this._summarizeReactions(card);
    return card;
  }

  /** Same rule as the feed's unread rows: activity after both the read floor and your last visit. */
  private _recomputeUnread() {
    const floor = this.communityService.postUnreadFloor(this.communityStringId);
    this.cards.forEach((card) => {
      const activity = floor ? this.communityService.postActivityMillis(card.post) : 0;
      const lastViewed = this.communityService.peekPostLastViewed(card.id);
      card.unread = !!activity && activity > floor && lastViewed !== undefined && activity > lastViewed;
    });
    this._recountNew();
  }

  private _recountNew() {
    this.newCount = this.cards.filter((c) => c.unread).length;
  }
}
